import * as React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { RATIO, TypographySize, SEMANTIC_TAG } from 'mm-theme-configuration/dist/consts';
import { useTheme } from '../../../../theming/useTheme';
import { CardComponentDataProps, CardComponentPropsFromLayout } from '../cards.utils';
import { WrapperForCard } from '../WrapperForCard';
import { Link } from '../../link/Link';
import { ConditionalVideoWrap } from '../../ConditionalVideoWrap';
import { Image } from '../../image/Image';
import { DecorationLine } from '../../DecorationLine';
import { CARD_CAPTION_CONTAINER_TYPES, CardCaptionContainer } from '../../CardCaptionContainer';
import { semanticCSS } from '../../../../typography/semanticTags';
import { createStyleForBoxShadow } from '../../../../theming/boxShadow';
import { BaseUnit } from '../../../../theming/baseUnitDefinition';
import { DynamicSemanticTag } from '../../../../typography/semanticTags/DynamicSemanticTag';
import { createImageAspectRatios } from '../../image/image.utils';

const numberOfLines = 3;
export const componentConfigThemeKey = 'verticalCard';

export type VerticalCardThemeProps = {
  cardBackgroundColor: string;
  cardTitleColorTypeNormal: string;
  cardTitleColorTypeHover: string;
  cardTitleColorTypeActive: string;
  borderRadius: number;
  boxShadowTypeNormal: string;
  boxShadowTypeActive: string;
  boxShadowTypeHover: string;
  borderWidthTypeNormal: number;
  borderWidthTypeHover: number;
  borderWidthTypeActive: number;
  borderColorTypeNormal: string;
  borderColorTypeHover: string;
  borderColorTypeActive: string;
  baseUnit: BaseUnit;
  aspectRatioLarge: RATIO;
  aspectRatioMedium: RATIO;
  aspectRatioSmall: RATIO;
  titleFontSizeLarge: TypographySize;
  titleFontSizeMedium: TypographySize;
  titleFontSizeSmall: TypographySize;
  shouldReverseImageAlignment: boolean;
  showDate: boolean;
  dateFontSizeLarge: TypographySize;
  dateFontSizeMedium: TypographySize;
  dateFontSizeSmall: TypographySize;
  dateColor: string;
  pipeColor: string;
  showAuthor: boolean;
  authorFontSizeLarge: TypographySize;
  authorFontSizeMedium: TypographySize;
  authorFontSizeSmall: TypographySize;
  authorColor: string;
  wrapperGapLarge: number;
  wrapperGapMedium: number;
  wrapperGapSmall: number;
  verticalGapLarge: number;
  verticalGapMedium: number;
  verticalGapSmall: number;
  horizontalGapLarge: number;
  horizontalGapMedium: number;
  horizontalGapSmall: number;
  titleSemanticTag: SEMANTIC_TAG;
  dateSemanticTag: SEMANTIC_TAG;
  authorSemanticTag: SEMANTIC_TAG;
};

type VerticalCardProps = CardComponentDataProps & CardComponentPropsFromLayout

const createStyle = (cardBackgroundColor: string, cardTextColorTypeNormal: string, cardTextColorTypeHover: string, cardTextColorTypeActive: string, borderRadius: number) => {
  return StyleSheet.create({
    text: {
      overflow: 'hidden',
    },
    wrapper: {
      width: '100%',
      height: '100%',
      textDecoration: 'none',
      color: cardTextColorTypeNormal,
      backgroundColor: cardBackgroundColor,
      '-webkit-mask-image': '-webkit-radial-gradient(white, black)',
      borderRadius: `${borderRadius}px`,
      position: 'relative',
      ':hover': {
        color: cardTextColorTypeHover,
      },
      ':active': {
        color: cardTextColorTypeActive,
      },
      display: 'inline-block',
    },
  });
};

export const VerticalCard: React.FunctionComponent<VerticalCardProps> = props => {
  const {
    coverType,
    lazyLoad = true,
    showDecorationLine = true,
    title,
    articleUrl,
    image,
    imageWidths,
  } = props;
  const {
    borderRadius,
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal,
    boxShadowTypeNormal,
    boxShadowTypeHover,
    boxShadowTypeActive,
    cardBackgroundColor,
    cardTitleColorTypeNormal,
    cardTitleColorTypeHover,
    cardTitleColorTypeActive,
    titleFontSizeLarge,
    titleFontSizeSmall,
    titleFontSizeMedium,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    showAuthor,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    showDate,
    aspectRatioLarge,
    aspectRatioMedium,
    aspectRatioSmall,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    authorColor,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    authorFontSizeLarge,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    authorFontSizeMedium,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    authorFontSizeSmall,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dateColor,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dateFontSizeLarge,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dateFontSizeMedium,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dateFontSizeSmall,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    horizontalGapLarge,
    horizontalGapMedium,
    horizontalGapSmall,
    verticalGapLarge,
    verticalGapMedium,
    verticalGapSmall,
    wrapperGapLarge,
    wrapperGapMedium,
    wrapperGapSmall,
    titleSemanticTag,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dateSemanticTag,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    authorSemanticTag,
  } = useTheme<VerticalCardThemeProps>(componentConfigThemeKey);
  const borderBoxShadow = {
    borderColorTypeActive,
    borderColorTypeHover,
    borderColorTypeNormal,
    borderWidthTypeActive,
    borderWidthTypeHover,
    borderWidthTypeNormal,
  };

  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal, boxShadowTypeHover, boxShadowTypeActive, borderBoxShadow });
  const style = createStyle(cardBackgroundColor, cardTitleColorTypeNormal, cardTitleColorTypeHover, cardTitleColorTypeActive, borderRadius);
  return (
    <WrapperForCard
      boxShadowStyle={boxShadowStyle}
      borderRadius={borderRadius}
      paddingLarge={wrapperGapLarge}
      paddingMedium={wrapperGapMedium}
      paddingSmall={wrapperGapSmall}
    >
      <Link
        className={css(
          style.wrapper,
          boxShadowStyle.style,
        )}
        href={articleUrl}
      >
        <ConditionalVideoWrap shouldWarp={coverType === 'video'}>
          <Image {...image} lazyLoad={lazyLoad} imageWidths={imageWidths} imageAspectRatios={createImageAspectRatios(aspectRatioSmall, aspectRatioMedium, aspectRatioLarge)} />
        </ConditionalVideoWrap>
        {showDecorationLine ? <DecorationLine /> : null}
        <CardCaptionContainer
          numberOfLines={numberOfLines}
          type={CARD_CAPTION_CONTAINER_TYPES.VERTICAL}
          horizontalMarginLarge={horizontalGapLarge}
          horizontalMarginMedium={horizontalGapMedium}
          horizontalMarginSmall={horizontalGapSmall}
          verticalMarginLarge={verticalGapLarge}
          verticalMarginMedium={verticalGapMedium}
          verticalMarginSmall={verticalGapSmall}
        >
          <DynamicSemanticTag
            semanticTag={titleSemanticTag}
            fontSizeLarge={titleFontSizeLarge}
            fontSizeMedium={titleFontSizeMedium}
            fontSizeSmall={titleFontSizeSmall}
            styles={semanticCSS(style.text)}
          >
            {title}
          </DynamicSemanticTag>
        </CardCaptionContainer>
      </Link>
    </WrapperForCard>
  );
};
